import React, { useEffect, useMemo, useState } from 'react'
import {
	Button, Col, DatePicker, Input, Modal, Progress, Row, Select, Spin, Statistic, Tabs, Tag
} from 'antd'
import { EditOutlined, PhoneOutlined, RocketOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import { RootState } from '../../reducers'
import { getJobs } from '../../reducers/jobs/actions'
import {
	createTelephoneSurvey,
	getTelephoneSurveys,
	ITelephoneSurveyForm,
	TELEPHONE_SURVEY_CALL_STATUS,
	TELEPHONE_SURVEY_SATISFACTION,
	updateTelephoneSurvey
} from '../../reducers/telephoneSurveys/actions'
import { history } from '../../utils/history'
import TelephoneSurveyCallCard from './components/TelephoneSurveyCallCard'

type JobPair = { address: string; cleanerName: string }

type OperatorTrustee = {
	trusteeId: number
	name: string
	phone: string
	firstName: string | null
	lastName: string | null
	email: string | null
	addresses: string[]
	cleanerNames: string[]
	jobPairs: JobPair[]
	jobIds: number[]
	lastContactDate: string | null
	lastContactTimestamp: number
	organizationName: string
}

const parseSurveyDate = (value?: string | null) => {
	if (!value) return 0
	const nativeTime = new Date(value).getTime()
	if (!Number.isNaN(nativeTime)) return nativeTime
	const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+(\d{1,2}):(\d{1,2})$/)
	if (!match) return 0
	const [, day, month, year, hour, minute] = match
	return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)).getTime()
}

const panelStyle: React.CSSProperties = {
	background: '#fff',
	border: '1px solid #e5e7eb',
	borderRadius: 8,
	boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)'
}

const PhoneOperatorSurveys = () => {
	const dispatch = useDispatch()
	const location = useLocation<{ selectedOrganization?: string, sortBy?: 'lastContact' | 'name' }>()
	const profile = useSelector((state: RootState) => state.profile.profile.data)
	const jobsPayload = useSelector((state: RootState) => state.jobs.jobs)
	const surveys = useSelector((state: RootState) => state.telephoneSurveys.telephoneSurveys)

	const isAutomaticRoute = location.pathname === '/prieskum-spokojnosti/telefonistka/automaticke-volanie'
	const [selectedOrganization, setSelectedOrganization] = useState(location.state?.selectedOrganization || 'Všetky organizácie')
	const [sortBy, setSortBy] = useState<'lastContact' | 'name'>(location.state?.sortBy || 'lastContact')
	const [mode, setMode] = useState<'manual' | 'automatic'>('manual')
	const [currentIndex, setCurrentIndex] = useState(0)
	const [selectedCustomer, setSelectedCustomer] = useState<OperatorTrustee | null>(null)
	const [form, setForm] = useState<ITelephoneSurveyForm>({
		jobIDs: [],
		callStatus: null,
		satisfaction: null,
		note: '',
		scheduledCallAt: null
	})

	const [activeTab, setActiveTab] = useState<'call' | 'surveys'>('call')
	const [editModalVisible, setEditModalVisible] = useState(false)
	const [editSurveyId, setEditSurveyId] = useState<number | null>(null)
	const [editJobIds, setEditJobIds] = useState<number[]>([])
	const [editAvailableJobs, setEditAvailableJobs] = useState<{ id: number, address: string }[]>([])
	const [isEditSaving, setIsEditSaving] = useState(false)
	const [editForm, setEditForm] = useState<{
		callStatus: TELEPHONE_SURVEY_CALL_STATUS | null
		satisfaction: TELEPHONE_SURVEY_SATISFACTION | null
		note: string
		scheduledCallAt: string | null
	}>({
		callStatus: null,
		satisfaction: null,
		note: '',
		scheduledCallAt: null
	})

	useEffect(() => {
		dispatch(getJobs({
			limit: 1000,
			page: 1,
			queryParams: { hasPhone: true }
		}))
		dispatch(getTelephoneSurveys({ limit: 1000, page: 1 }))
	}, [dispatch])

	const latestSurveyByJob = useMemo(() => {
		const latest = new Map<number, any>()
		surveys.originalData.forEach((survey: any) => {
			(survey.jobs || []).forEach((job: any) => {
				const current = latest.get(job.id)
				if (!current || parseSurveyDate(survey.calledAt || survey.createdAt) > parseSurveyDate(current.calledAt || current.createdAt)) {
					latest.set(job.id, survey)
				}
			})
		})
		return latest
	}, [surveys.originalData])

	const customers = useMemo<OperatorTrustee[]>(() => {
		const trusteeMap = new Map<number, OperatorTrustee>()
		const originalJobs = (jobsPayload.originalData || []) as any[]

		originalJobs.forEach((job) => {
			const trustee = (job.trustees || []).find((item: any) => item.phone) || (job.trustees || [])[0]
			if (!trustee?.phone || !trustee?.id) return
			const latestSurvey = latestSurveyByJob.get(job.id)
			const cleanerName = job.user ? `${job.user.firstName || ''} ${job.user.lastName || ''}`.trim() : ''
			const existing = trusteeMap.get(trustee.id)

			if (!existing) {
				trusteeMap.set(trustee.id, {
					trusteeId: trustee.id,
					name: trustee.name || job.address || 'Zákazník',
					phone: trustee.phone,
					firstName: trustee.firstName || null,
					lastName: trustee.lastName || null,
					email: trustee.email || null,
					addresses: job.address ? [job.address] : [],
					cleanerNames: cleanerName ? [cleanerName] : [],
					jobPairs: job.address ? [{ address: job.address, cleanerName }] : [],
					jobIds: [job.id],
					lastContactDate: latestSurvey?.calledAt || latestSurvey?.createdAt || null,
					lastContactTimestamp: parseSurveyDate(latestSurvey?.calledAt || latestSurvey?.createdAt),
					organizationName: job.organization?.name || 'Bez organizácie'
				})
				return
			}

			existing.jobIds.push(job.id)
			if (job.address && !existing.addresses.includes(job.address)) {
				existing.addresses.push(job.address)
				existing.jobPairs.push({ address: job.address, cleanerName })
			}
			if (cleanerName && !existing.cleanerNames.includes(cleanerName)) {
				existing.cleanerNames.push(cleanerName)
			}

			const latestTimestamp = parseSurveyDate(latestSurvey?.calledAt || latestSurvey?.createdAt)
			if (latestTimestamp > existing.lastContactTimestamp) {
				existing.lastContactTimestamp = latestTimestamp
				existing.lastContactDate = latestSurvey?.calledAt || latestSurvey?.createdAt || null
			}
		})

		return Array.from(trusteeMap.values())
	}, [jobsPayload.originalData, latestSurveyByJob])

	const organizations = useMemo(() => [
		'Všetky organizácie',
		...Array.from(new Set(customers.map((customer) => customer.organizationName)))
	], [customers])

	const sortedCustomers = useMemo(() => customers
		.filter((customer) => selectedOrganization === 'Všetky organizácie' || customer.organizationName === selectedOrganization)
		.sort((a, b) => {
			if (sortBy === 'name') return a.name.localeCompare(b.name, 'sk')
			if (!a.lastContactTimestamp && b.lastContactTimestamp) return -1
			if (a.lastContactTimestamp && !b.lastContactTimestamp) return 1
			return a.lastContactTimestamp - b.lastContactTimestamp
		}), [customers, selectedOrganization, sortBy])

	const surveyedCustomers = useMemo(
		() => customers
			.filter((customer) => (selectedOrganization === 'Všetky organizácie' || customer.organizationName === selectedOrganization)
				&& customer.jobIds.some((jobId) => latestSurveyByJob.has(jobId)))
			.sort((a, b) => b.lastContactTimestamp - a.lastContactTimestamp),
		[customers, selectedOrganization, latestSurveyByJob]
	)

	const getLatestSurveyForCustomer = (customer: OperatorTrustee | null) => {
		if (!customer) return null

		return customer.jobIds.reduce((latestSurvey: any, jobId) => {
			const currentSurvey = latestSurveyByJob.get(jobId)
			if (!currentSurvey) return latestSurvey
			if (!latestSurvey) return currentSurvey
			return parseSurveyDate(currentSurvey.calledAt || currentSurvey.createdAt) > parseSurveyDate(latestSurvey.calledAt || latestSurvey.createdAt)
				? currentSurvey
				: latestSurvey
		}, null)
	}

	const todayCount = surveys.originalData.filter((survey: any) => dayjs(parseSurveyDate(survey.calledAt)).isSame(dayjs(), 'day')).length
	const weekCount = surveys.originalData.filter((survey: any) => dayjs(parseSurveyDate(survey.calledAt)).isSame(dayjs(), 'week')).length

	useEffect(() => {
		if (!isAutomaticRoute) {
			setMode('manual')
			setSelectedCustomer(null)
			setCurrentIndex(0)
		} else {
			setMode('automatic')
		}
	}, [isAutomaticRoute])

	useEffect(() => {
		if (isAutomaticRoute && !selectedCustomer && sortedCustomers.length) {
			const firstCustomer = sortedCustomers[0]
			setSelectedCustomer(firstCustomer)
			setCurrentIndex(0)
			setForm({
				jobIDs: firstCustomer.jobIds,
				callStatus: null,
				satisfaction: null,
				note: '',
				scheduledCallAt: null
			})
		}
	}, [isAutomaticRoute, selectedCustomer, sortedCustomers])

	const selectCustomer = (customer: OperatorTrustee, index = 0) => {
		setSelectedCustomer(customer)
		setCurrentIndex(index)
		setForm({
			jobIDs: customer.jobIds,
			callStatus: null,
			satisfaction: null,
			note: '',
			scheduledCallAt: null
		})
	}

	const handlePreviousCustomer = () => {
		const prevIndex = currentIndex - 1
		if (prevIndex < 0) return
		selectCustomer(sortedCustomers[prevIndex], prevIndex)
	}

	const openEditModal = (customer: OperatorTrustee) => {
		const lastSurvey = getLatestSurveyForCustomer(customer)
		if (!lastSurvey) return
		setEditSurveyId(lastSurvey.id)
		setEditJobIds((lastSurvey.jobs || []).map((job: any) => job.id))
		setEditAvailableJobs(customer.jobIds.map((jobId, index) => ({
			id: jobId,
			address: customer.addresses[index] || `Job ${jobId}`
		})))
		setEditForm({
			callStatus: lastSurvey.callStatus || null,
			satisfaction: lastSurvey.satisfaction || null,
			note: lastSurvey.note || '',
			scheduledCallAt: lastSurvey.scheduledCallAt || null
		})
		setEditModalVisible(true)
	}

	const handleOpenEditModal = () => {
		if (!selectedCustomer) return
		openEditModal(selectedCustomer)
	}

	const handleEditSave = () => {
		if (!editSurveyId || !editForm.callStatus || !editJobIds.length) return
		setIsEditSaving(true)
		dispatch(updateTelephoneSurvey(
			editSurveyId,
			{
				jobIDs: editJobIds,
				callStatus: editForm.callStatus,
				satisfaction: editForm.callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER ? null : editForm.satisfaction,
				note: editForm.note,
				scheduledCallAt: editForm.scheduledCallAt
			},
			() => {
				dispatch(getTelephoneSurveys({ limit: 1000, page: 1 }))
				setEditModalVisible(false)
				setIsEditSaving(false)
			},
			() => setIsEditSaving(false)
		))
	}

	const startAutomaticMode = () => {
		if (!sortedCustomers.length) return
		history.push('/prieskum-spokojnosti/telefonistka/automaticke-volanie', {
			selectedOrganization,
			sortBy
		})
	}

	const handleSkipCustomer = () => {
		const nextIndex = currentIndex + 1
		if (nextIndex >= sortedCustomers.length) {
			setMode('manual')
			setSelectedCustomer(null)
			setCurrentIndex(0)
			if (isAutomaticRoute) {
				history.push('/prieskum-spokojnosti/telefonistka')
			}
			return
		}
		selectCustomer(sortedCustomers[nextIndex], nextIndex)
	}

	const handleSaveSurvey = (values: Partial<ITelephoneSurveyForm> = {}) => {
		const nextForm = {
			...form,
			...values
		}
		if (!selectedCustomer || !selectedCustomer.jobIds.length || !nextForm.callStatus) return
		if (nextForm.callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER && !nextForm.scheduledCallAt) return
		dispatch(createTelephoneSurvey({
			...nextForm,
			jobIDs: selectedCustomer.jobIds
		}, () => {
			dispatch(getTelephoneSurveys({ limit: 1000, page: 1 }))
			if (mode === 'automatic') {
				handleSkipCustomer()
				return
			}
			setSelectedCustomer(null)
			setActiveTab('call')
		}))
	}

	return (
		<div className={'page-wrapper'} style={{
			background: '#f3f4f6', minHeight: '100%', padding: 0, marginTop: 0
		}}>
			{!isAutomaticRoute && (
				<div style={{
					background: '#337ab7',
					color: '#fff',
					padding: '22px 32px',
					margin: '0 -16px',
					boxShadow: '0 8px 20px rgba(15, 23, 42, 0.12)'
				}}
				>
					<Row align={'middle'} gutter={32}>
						<Col span={8}>
							<h1 style={{
								margin: 0, color: '#fff', fontSize: 28, lineHeight: '33px', fontWeight: 800
							}}>
								Telefonické<br />prieskumy
							</h1>
							<div style={{
								marginTop: 10, color: '#dbeafe', fontSize: 14, fontWeight: 700
							}}>
								Operátor: {profile?.firstName || ''} {profile?.lastName || ''}
							</div>
						</Col>
						<Col span={8}>
							<Button
								block
								icon={<RocketOutlined />}
								onClick={startAutomaticMode}
								disabled={!sortedCustomers.length}
								style={{
									height: 56,
									borderRadius: 8,
									border: 0,
									background: '#00c43b',
									color: '#fff',
									fontSize: 15,
									fontWeight: 800,
									whiteSpace: 'normal'
								}}
							>
								Spustiť automatické volanie
							</Button>
						</Col>
						<Col span={4}>
							<Statistic title={<span style={{ color: '#dbeafe', fontSize: 13 }}>Dnes obvolaných</span>} value={todayCount} valueStyle={{ color: '#fff', fontSize: 24, fontWeight: 800 }} />
						</Col>
						<Col span={4}>
							<Statistic title={<span style={{ color: '#dbeafe', fontSize: 13 }}>Tento týždeň</span>} value={weekCount} valueStyle={{ color: '#fff', fontSize: 24, fontWeight: 800 }} />
						</Col>
					</Row>
				</div>
			)}

			<div style={{ maxWidth: 1440, margin: '0 auto', padding: '20px 32px' }}>

				{selectedCustomer && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
						{mode === 'manual' && (
							<div style={{
								...panelStyle, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
							}}>
								<div>
									<h3 style={{
										margin: 0, color: '#1f2937', fontSize: 15, fontWeight: 700
									}}>
										Voláte zákazníkovi
									</h3>
									<p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: 13 }}>
										{selectedCustomer.name}{selectedCustomer.organizationName ? ` · ${selectedCustomer.organizationName}` : ''}
									</p>
								</div>
								<Button
									size={'small'}
									type={'link'}
									onClick={() => { setSelectedCustomer(null); setActiveTab('call') }}
									style={{ color: '#6b7280', fontWeight: 700 }}
								>
									← Späť na zoznam
								</Button>
							</div>
						)}
						{mode === 'automatic' && (
							<div style={{ ...panelStyle, padding: 14 }}>
								<div style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: 14,
									marginBottom: 8
								}}
								>
									<div>
										<h3 style={{
											margin: 0, color: '#1f2937', fontSize: 15, fontWeight: 700
										}}>
											Automatický režim - Postupné volanie
										</h3>
										<p style={{ margin: '3px 0 0', color: '#6b7280', fontSize: 13 }}>
											Zákazník {currentIndex + 1} z {sortedCustomers.length}
										</p>
									</div>
									<div style={{ display: 'flex', gap: 8 }}>
										{currentIndex > 0 && (
											<Button size={'small'} type={'link'} onClick={handlePreviousCustomer} style={{ color: '#6b7280', fontWeight: 700 }}>
												← Späť
											</Button>
										)}
										<Button size={'small'} type={'link'} onClick={handleSkipCustomer} style={{ color: '#f97316', fontWeight: 700 }}>
											Preskočiť →
										</Button>
									</div>
								</div>
								<Progress percent={Math.round(((currentIndex + 1) / sortedCustomers.length) * 100)} showInfo={false} strokeColor={'#337ab7'} />
							</div>
						)}

						<TelephoneSurveyCallCard
							key={`${currentIndex}-${selectedCustomer.trusteeId ?? selectedCustomer.name}`}
							title={'Telefonický prieskum'}
							jobPairs={selectedCustomer.jobPairs}
							contactName={selectedCustomer.name}
							contactPhone={selectedCustomer.phone}
							contactEmail={null}
							organizationName={selectedCustomer.organizationName}
							callStatus={form.callStatus}
							satisfaction={form.satisfaction}
							note={form.note || ''}
							scheduledCallAt={form.scheduledCallAt}
							trusteeUserID={selectedCustomer.trusteeId}
							trusteeFirstName={selectedCustomer.firstName}
							trusteeLastName={selectedCustomer.lastName}
							trusteeEmail={selectedCustomer.email}
							onTrusteeUpdated={(field, value) => {
								setSelectedCustomer((prev) => {
									if (!prev) return prev
									const updated = { ...prev, [field]: value }
									if (field === 'firstName' || field === 'lastName') {
										const first = field === 'firstName' ? value : (prev.firstName || '')
										const last = field === 'lastName' ? value : (prev.lastName || '')
										updated.name = [first, last].filter(Boolean).join(' ') || prev.name
									}
									return updated
								})
								dispatch(getJobs({ limit: 1000, page: 1, queryParams: { hasPhone: true } }))
							}}
							onEditSurvey={getLatestSurveyForCustomer(selectedCustomer) ? handleOpenEditModal : undefined}
							onCallStatusChange={(callStatus) => setForm((prev) => ({
								...prev,
								callStatus,
								satisfaction: callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER ? null : prev.satisfaction,
								scheduledCallAt: callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER ? prev.scheduledCallAt : null
							}))}
							onSatisfactionChange={(satisfaction) => setForm((prev) => ({ ...prev, satisfaction }))}
							onNoteChange={(note) => setForm((prev) => ({ ...prev, note }))}
							onScheduledCallAtChange={(scheduledCallAt) => setForm((prev) => ({ ...prev, scheduledCallAt }))}
							onSubmit={handleSaveSurvey}
							onCancel={() => {
								setSelectedCustomer(null)
								if (mode === 'automatic') {
									setMode('manual')
									setCurrentIndex(0)
									if (isAutomaticRoute) {
										history.push('/prieskum-spokojnosti/telefonistka')
									}
								} else {
									setActiveTab('call')
								}
							}}
						/>
					</div>
				)}

				{!selectedCustomer && isAutomaticRoute && (
					<div style={{
						...panelStyle, padding: 24, color: '#6b7280', textAlign: 'center'
					}}>
						{jobsPayload.isLoading ? 'Načítavam zákazníka na obvolanie...' : 'Nie je dostupný žiadny zákazník na automatické volanie.'}
					</div>
				)}

				{!selectedCustomer && !isAutomaticRoute && (
					<>
						<div style={{ ...panelStyle, padding: 14, marginBottom: 16 }}>
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: 18
							}}
							>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
									<label style={{ color: '#374151', fontSize: 14, fontWeight: 700 }}>Organizácia:</label>
									<Select
										value={selectedOrganization}
										onChange={(value) => setSelectedOrganization(String(value))}
										style={{ minWidth: 190 }}
									>
										{organizations.map((organization) => (
											<Select.Option key={organization} value={organization}>{organization}</Select.Option>
										))}
									</Select>
								</div>
								{activeTab === 'call' && (
									<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
										<label style={{ color: '#374151', fontSize: 14, fontWeight: 700 }}>Zoradiť:</label>
										<Select
											value={sortBy}
											onChange={(value) => setSortBy(value as 'lastContact' | 'name')}
											style={{ minWidth: 330 }}
										>
											<Select.Option value={'lastContact'}>Podľa posledného kontaktu</Select.Option>
											<Select.Option value={'name'}>Podľa mena</Select.Option>
										</Select>
									</div>
								)}
							</div>
						</div>

						<Tabs
							activeKey={activeTab}
							onChange={(key) => setActiveTab(key as 'call' | 'surveys')}
							style={{ ...panelStyle, padding: '0 20px' }}
						>
							<Tabs.TabPane
								tab={<span style={{ fontWeight: 700 }}>📞 Volať zákazníkom ({sortedCustomers.length})</span>}
								key={'call'}
							>
								<div style={{
									background: '#eff6ff',
									border: '1px solid #bfdbfe',
									borderRadius: 8,
									padding: 14,
									marginBottom: 16,
									display: 'flex',
									gap: 12
								}}
								>
									<span style={{ color: '#2563eb', fontSize: 18 }}>ℹ</span>
									<div>
										<h4 style={{
											margin: '0 0 6px', color: '#1e3a8a', fontSize: 15, fontWeight: 800
										}}>Inštrukcie</h4>
										<p style={{ margin: '0 0 4px', color: '#1e40af', fontSize: 13 }}>
											<strong>Manuálny režim:</strong> Kliknite na zákazníka pre začatie telefonického prieskumu.
										</p>
										<p style={{ margin: 0, color: '#1e40af', fontSize: 13 }}>
											<strong>Automatický režim:</strong> Kliknite na &quot;Spustiť automatické volanie&quot; v hlavičke a systém vám bude postupne zobrazovať zákazníkov na obvolanie. Po uložení prieskumu sa automaticky zobrazí ďalší zákazník.
										</p>
									</div>
								</div>

								<Spin spinning={jobsPayload.isLoading}>
									{sortedCustomers.length === 0 ? (
										<div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>
											Žiadni zákazníci pre vybranú organizáciu
										</div>
									) : sortedCustomers.map((customer, index) => (
										<div
											key={customer.trusteeId}
											style={{
												padding: '14px 0',
												borderTop: '1px solid #e5e7eb'
											}}
										>
											<div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
												<div style={{ flex: 1 }}>
													<div style={{
														display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10
													}}>
														<h3 style={{
															margin: 0, color: '#1f2937', fontSize: 16, fontWeight: 800
														}}>{customer.name}</h3>
														{!customer.lastContactDate && (
															<span style={{
																background: '#fef3c7',
																color: '#92400e',
																borderRadius: 999,
																padding: '3px 9px',
																fontSize: 12,
																fontWeight: 700
															}}
															>
																Nekontaktovaný
															</span>
														)}
													</div>
													<Row gutter={[24, 18]}>
														<Col span={10}>
															<div style={{ color: '#6b7280', fontSize: 13 }}>Adresa:</div>
															<div style={{ color: '#1f2937', fontSize: 14 }}>{customer.addresses.join(', ')}</div>
														</Col>
														<Col span={10}>
															<div style={{ color: '#6b7280', fontSize: 13 }}>Telefón:</div>
															<div style={{ color: '#1f2937', fontSize: 14, fontWeight: 700 }}>{customer.phone}</div>
														</Col>
														<Col span={10}>
															<div style={{ color: '#6b7280', fontSize: 13 }}>Upratovačka:</div>
															<div style={{ color: '#1f2937', fontSize: 14 }}>{customer.cleanerNames.join(', ') || '-'}</div>
														</Col>
														<Col span={10}>
															<div style={{ color: '#6b7280', fontSize: 13 }}>Posledný kontakt:</div>
															<div style={{ color: '#1f2937', fontSize: 14 }}>{customer.lastContactDate || 'Nikdy'}</div>
														</Col>
													</Row>
												</div>
												<Button
													type={'primary'}
													icon={<PhoneOutlined />}
													onClick={(e) => { e.stopPropagation(); selectCustomer(customer, index) }}
													style={{
														alignSelf: 'center',
														height: 38,
														padding: '0 18px',
														borderRadius: 8,
														background: '#337ab7',
														borderColor: '#337ab7',
														fontSize: 14,
														fontWeight: 800
													}}
												>
													Zavolať
												</Button>
											</div>
										</div>
									))}
								</Spin>
							</Tabs.TabPane>

							<Tabs.TabPane
								tab={<span style={{ fontWeight: 700 }}>✅ Realizované prieskumy ({surveyedCustomers.length})</span>}
								key={'surveys'}
							>
								<Spin spinning={jobsPayload.isLoading || surveys.isLoading}>
									{surveyedCustomers.length === 0 ? (
										<div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>
											Žiadne realizované prieskumy pre vybranú organizáciu
										</div>
									) : surveyedCustomers.map((customer) => {
										const lastSurvey = getLatestSurveyForCustomer(customer)
										return (
											<div
												key={customer.trusteeId}
												style={{
													padding: '14px 0',
													borderTop: '1px solid #e5e7eb'
												}}
											>
												<div style={{
													display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'center'
												}}>
													<div style={{ flex: 1 }}>
														<div style={{
															display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap'
														}}>
															<h3 style={{
																margin: 0, color: '#1f2937', fontSize: 16, fontWeight: 800
															}}>{customer.name}</h3>
															<span style={{ color: '#6b7280', fontSize: 13 }}>{customer.organizationName}</span>
															{lastSurvey?.callStatus === TELEPHONE_SURVEY_CALL_STATUS.ANSWERED && (
																<Tag color={'green'} style={{ margin: 0 }}>✓ Zodvihol</Tag>
															)}
															{lastSurvey?.callStatus === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER && (
																<Tag color={'orange'} style={{ margin: 0 }}>× Nezodvihol</Tag>
															)}
															{lastSurvey?.callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER && (
																<Tag color={'blue'} style={{ margin: 0 }}>⏰ Zavolať neskôr</Tag>
															)}
															{lastSurvey?.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED && (
																<Tag color={'gold'} style={{ margin: 0 }}>☀ Spokojný</Tag>
															)}
															{lastSurvey?.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED && (
																<Tag color={'red'} style={{ margin: 0 }}>✗ Nespokojný</Tag>
															)}
														</div>
														<Row gutter={[24, 6]}>
															<Col span={10}>
																<span style={{ color: '#6b7280', fontSize: 13 }}>Adresa: </span>
																<span style={{ color: '#1f2937', fontSize: 13 }}>{customer.addresses.join(', ')}</span>
															</Col>
															<Col span={10}>
																<span style={{ color: '#6b7280', fontSize: 13 }}>Telefón: </span>
																<span style={{ color: '#1f2937', fontSize: 13, fontWeight: 700 }}>{customer.phone}</span>
															</Col>
															<Col span={10}>
																<span style={{ color: '#6b7280', fontSize: 13 }}>Upratovačka: </span>
																<span style={{ color: '#1f2937', fontSize: 13 }}>{customer.cleanerNames.join(', ') || '-'}</span>
															</Col>
															<Col span={10}>
																<span style={{ color: '#6b7280', fontSize: 13 }}>Posledný kontakt: </span>
																<span style={{ color: '#1f2937', fontSize: 13, fontWeight: 600 }}>{customer.lastContactDate || '-'}</span>
															</Col>
														</Row>
														{lastSurvey?.note && (
															<div style={{
																marginTop: 6, color: '#4b5563', fontSize: 12, fontStyle: 'italic'
															}}>
																&bdquo;{lastSurvey.note}&ldquo;
															</div>
														)}
													</div>
													<Button
														icon={<EditOutlined />}
														onClick={() => openEditModal(customer)}
														style={{
															alignSelf: 'center',
															borderRadius: 8,
															borderColor: '#d1d5db',
															color: '#374151'
														}}
													>
														Upraviť
													</Button>
												</div>
											</div>
										)
									})}
								</Spin>
							</Tabs.TabPane>
						</Tabs>
					</>
				)}
			</div>

			<Modal
				title={'Upraviť prieskum'}
				visible={editModalVisible}
				onCancel={() => setEditModalVisible(false)}
				onOk={handleEditSave}
				okText={'Uložiť'}
				cancelText={'Zrušiť'}
				confirmLoading={isEditSaving}
				okButtonProps={{ disabled: !editForm.callStatus }}
			>
				<div style={{ marginBottom: 16 }}>
					<label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Joby v prieskume</label>
					<Select
						mode={'multiple'}
						value={editJobIds}
						onChange={(value) => setEditJobIds(value as number[])}
						style={{ width: '100%', marginBottom: 12 }}
						placeholder={'Vyberte joby'}
					>
						{editAvailableJobs.map((job) => (
							<Select.Option key={job.id} value={job.id}>{job.address}</Select.Option>
						))}
					</Select>

					<label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Stav hovoru</label>
					<Select
						value={editForm.callStatus}
						onChange={(v) => setEditForm((prev) => ({
							...prev,
							callStatus: v,
							satisfaction: v === TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER ? null : prev.satisfaction,
							scheduledCallAt: v !== TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER ? null : prev.scheduledCallAt
						}))}
						style={{ width: '100%' }}
					>
						<Select.Option value={TELEPHONE_SURVEY_CALL_STATUS.ANSWERED}>✓ Zodvihol</Select.Option>
						<Select.Option value={TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER}>× Nezodvihol</Select.Option>
						<Select.Option value={TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER}>⏰ Zavolať neskôr</Select.Option>
					</Select>
				</div>

				{editForm.callStatus === TELEPHONE_SURVEY_CALL_STATUS.CALL_LATER && (
					<div style={{ marginBottom: 16 }}>
						<label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Dátum a čas spätného volania</label>
						<DatePicker
							showTime={{ format: 'HH:mm' }}
							value={editForm.scheduledCallAt ? dayjs(editForm.scheduledCallAt) : null}
							onChange={(v) => setEditForm((prev) => ({ ...prev, scheduledCallAt: v ? v.toISOString() : null }))}
							style={{ width: '100%' }}
							placeholder={'Vybrať dátum a čas'}
						/>
					</div>
				)}

				{editForm.callStatus && editForm.callStatus !== TELEPHONE_SURVEY_CALL_STATUS.NO_ANSWER && (
					<div style={{ marginBottom: 16 }}>
						<label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Hodnotenie spokojnosti</label>
						<div style={{ display: 'flex', gap: 10 }}>
							<Button
								block
								onClick={() => setEditForm((prev) => ({ ...prev, satisfaction: TELEPHONE_SURVEY_SATISFACTION.SATISFIED }))}
								style={{
									height: 44,
									borderRadius: 8,
									fontWeight: 700,
									background: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED ? '#facc15' : '#fff',
									borderColor: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED ? '#facc15' : '#d1d5db',
									color: '#1f2937',
									boxShadow: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.SATISFIED ? '0 2px 6px rgba(250,204,21,0.4)' : 'none'
								}}
							>
								☀ Spokojný
							</Button>
							<Button
								block
								onClick={() => setEditForm((prev) => ({ ...prev, satisfaction: TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED }))}
								style={{
									height: 44,
									borderRadius: 8,
									fontWeight: 700,
									background: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED ? '#ef4444' : '#fff',
									borderColor: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED ? '#ef4444' : '#d1d5db',
									color: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED ? '#fff' : '#1f2937',
									boxShadow: editForm.satisfaction === TELEPHONE_SURVEY_SATISFACTION.UNSATISFIED ? '0 2px 6px rgba(239,68,68,0.4)' : 'none'
								}}
							>
								✗ Nespokojný
							</Button>
						</div>
					</div>
				)}

				<div>
					<label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Poznámka</label>
					<Input.TextArea
						value={editForm.note}
						onChange={(e) => setEditForm((prev) => ({ ...prev, note: e.target.value }))}
						rows={4}
						placeholder={'Poznámka k hovoru...'}
					/>
				</div>
			</Modal>
		</div>
	)
}

export default PhoneOperatorSurveys
